import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Grid from "@mui/material/Grid2";
import { useFormik } from "formik";
import * as Yup from "yup";
import { patch, uploadFile } from "../../../services/apiService";
import BackdropLoader from "../../common/BackdropLoader";
import { Link } from "react-router-dom";
import i18next from "i18next";

export default function UserInformation({
  onUserContinue,
  screenWidth,
  userInfo,
}) {
  const isArabic = i18next.language === "ar";
  const [isLoading, setIsLoading] = useState(false);
  const [errorsObject, setErrorsObject] = useState({
    crDocument: "",
    iban: "",
    nationalId: "",
  });
  const initialData = {
    fullName: userInfo.fullName,
    phoneNumber: userInfo.phoneNumber,
    email: userInfo.email,
  };

  const countries = [
    { code: "SAR", name: "Saudi Arabia" },
    { code: "US", name: "United States" },
    { code: "CA", name: "Canada" },
    { code: "FR", name: "France" },
    { code: "DE", name: "Germany" },
    { code: "IN", name: "India" },
  ];

  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      // .matches(/^[A-Za-z\s]+$/, "Full name must contain only letters")
      // .min(3, "Full name must be at least 3 characters")
      .required("Full Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    stateProvince: Yup.string().required("Province is required"),
    postalCode: Yup.string().required("Postal Code is required"),
    city: Yup.string().required("City is required"),
    phoneNumber: Yup.string().required("Phone Number is required"),
    country: Yup.string().required("Country is required"),
    address: Yup.string().required("Address is required"),
    isMerchant: Yup.boolean(),

    privacyPolicy: Yup.boolean().oneOf(
      [true],
      "You must accept the privacy policy"
    ),
  });

  const formik = useFormik({
    initialValues: {
      fullName: userInfo.fullName,
      email: userInfo.email,
      phoneNumber: userInfo.phoneNumber,
      country: userInfo.country || "",
      address: userInfo.address || "",
      city: userInfo.city || "",
      stateProvince: userInfo.stateProvince || "",
      postalCode: userInfo.postalCode || "",
      crDocument: {
        fileName: "",
        uploadedFileUrl: "",
      },
      iban: {
        fileName: "",
        uploadedFileUrl: "",
      },
      nationalId: {
        fileName: "",
        uploadedFileUrl: "",
      },
      privacyPolicy: false,
      isMerchant: false,
    },
    validationSchema,
    onSubmit: async (values, errors) => {
      const newError = { crDocument: "", iban: "", nationalId: "" };
      if (values.isMerchant) {
        if (!values.crDocument.fileName) {
          newError.crDocument = "CR Document is required";
        }
        if (!values.iban.fileName) {
          newError.iban = "IBAN Document is required";
        }
        if (!values.nationalId.fileName) {
          newError.nationalId = "National Id Document is required";
        }
        setErrorsObject(newError);
      }
      if (
        !values.isMerchant ||
        (values.isMerchant &&
          values.crDocument.uploadFileUrl &&
          values.iban.uploadFileUrl &&
          values.nationalId.uploadFileUrl)
      ) {
        let documents = [];
        if (values.isMerchant) {
          documents = [
            {
              documentName: values?.crDocument,
              documentUrl: values.crDocument.uploadFileUrl,
            },
            {
              documentName: values?.iban,
              documentUrl: values?.iban?.uploadFileUrl,
            },
            {
              documentName: values?.nationalId,
              documentUrl: values.nationalId.uploadFileUrl,
            },
          ];
        }

        let saveObject = {
          country: values.country,
          address: values.address,
          stateProvince: values.stateProvince,
          city: values.city,
          postalCode: Number(values.postalCode),
        };
        saveObject = values.isMerchant
          ? { ...saveObject, documents: documents }
          : saveObject;
        if (initialData.email !== values.email) {
          saveObject.email = values.email;
        }
        if (initialData.phoneNumber !== values.phoneNumber) {
          saveObject.phoneNumber = values.phoneNumber;
        }
        // Check if fullName has changed before adding to saveObject
        if (initialData.fullName !== values.fullName) {
          saveObject.fullName = values.fullName;
        }

        const response = await patch(
          `users/configure/information/${userInfo.id}`,
          saveObject,
          {},
          onLoading
        );
        if (response.status === 200) {
          onUserContinue();
        }
      }
    },
  });
  // Formik and Yup Validation Schema

  const onLoading = (isLoading) => {
    setIsLoading(isLoading);
  };

  const handleDocumentUpload = async (event, field) => {
    const file = event.target.files[0];
    if (file) {
      setIsLoading(true);

      try {
        const response = await uploadFile("upload", file);
        formik.setFieldValue(field, {
          fileName: file.name,
          uploadFileUrl: response.url,
        });
      } catch (error) {
        console.error("File upload failed:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };



  return (
    <form onSubmit={formik.handleSubmit}>
      <Box
        sx={{
          backgroundColor: "linear-gradient(135deg, #f0f8ff, #e0f7ff)",
          borderRadius: 2,
          paddingLeft: screenWidth > 650 ? "15%" : "4%",
          paddingRight: screenWidth > 650 ? "15%" : "4%",
        }}
      >
        {isLoading && <BackdropLoader />}
        <Grid container spacing={2}>
          {/* Full Name */}
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,
                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_FULL_NAME")}
            </Typography>
            <TextField
              fullWidth
              placeholder={i18next.t("Bandr M. Alhdethe")}
              sx={{
                mb: 1,
                fontWeight: "bold",
                fontFamily: "Tajawal-Regular",
                "& .MuiInputBase-input::placeholder": {
                  color: "gray", // Set the placeholder color
                },
              }}
              {...formik.getFieldProps("fullName")}
              error={formik.touched.fullName && Boolean(formik.errors.fullName)}
              helperText={
                formik.touched.fullName && i18next.t(formik.errors.fullName)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_EMAIL")}
            </Typography>
            <TextField
              fullWidth
              placeholder="bandrealhadid@gmail.com"
              {...formik.getFieldProps("email")}
              sx={{ mb: 1, fontWeight: "bold", fontFamily: "Tajawal-Regular" }}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={
                formik.touched.email && i18next.t(formik.errors.email)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>
          <Grid item size={{ md: 6, sm: 12, xs: 12 }} mt={2}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_COUNTRY")}
            </Typography>
            <Select
              fullWidth
              labelId="country-select-label"
              id="country"
              name="country"
              value={formik.values.country}
              onChange={formik.handleChange}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    textAlign: isArabic ? "right" : "left",
                    direction: isArabic ? "rtl" : "ltr",
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: formik.errors.country ? "#d32f2f" : "grey", // Red if error, default otherwise
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: formik.errors.country ? "#d32f2f" : "grey", // Red on hover if error
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: formik.errors.country ? "#d32f2f" : "#1976d2", // if error on focused then color is red, otherwise blue
                },
              }}
            >
              <MenuItem value="" sx={{ color: "grey" }}>
                {i18next.t("Select Country")}
              </MenuItem>
              {countries.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {i18next.t(item.name)}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.country && formik.errors.country && (
              <FormHelperText
                sx={{
                  color: "#d32f2f",
                  marginLeft: "2%",
                }}
              >
                {formik.errors.country}
              </FormHelperText>
            )}
          </Grid>

          {/* Phone Number */}
          <Grid item size={{ md: 6, sm: 12, xs: 12 }} mt={2}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_PHONE_NUMBER")}
              {/* Phone Number */}
            </Typography>
            <TextField
              fullWidth
              placeholder="+9663234314270"
              sx={{
                mb: 1,
                fontWeight: "bold",
                fontFamily: "Tajawal-Regular",
                "& .MuiInputBase-input::placeholder": {
                  color: "gray", // Set the placeholder color
                },
              }}
              {...formik.getFieldProps("phoneNumber")}
              error={
                formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)
              }
              helperText={
                formik.touched.phoneNumber &&
                i18next.t(formik.errors.phoneNumber)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_CITY")}
            </Typography>
            <TextField
              fullWidth
              placeholder={i18next.t("Enter City")}
              {...formik.getFieldProps("city")}
              sx={{ mb: 1, fontWeight: "bold", fontFamily: "Tajawal-Regular" }}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && i18next.t(formik.errors.city)}
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_PROVINCE")}
            </Typography>
            <TextField
              fullWidth
              placeholder={i18next.t("Enter Province")}
              {...formik.getFieldProps("stateProvince")}
              sx={{ mb: 1, fontWeight: "bold", fontFamily: "Tajawal-Regular" }}
              error={
                formik.touched.stateProvince &&
                Boolean(formik.errors.stateProvince)
              }
              helperText={
                formik.touched.stateProvince &&
                i18next.t(formik.errors.stateProvince)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_POSTAL_CODE")}
            </Typography>
            <TextField
              fullWidth
              placeholder={i18next.t("Enter Postal Code")}
              {...formik.getFieldProps("postalCode")}
              sx={{ mb: 1, fontWeight: "bold", fontFamily: "Tajawal-Regular" }}
              error={
                formik.touched.postalCode && Boolean(formik.errors.postalCode)
              }
              helperText={
                formik.touched.postalCode && i18next.t(formik.errors.postalCode)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>

          {/* Country */}

          {/* Address */}
          <Grid item size={{ md: 6, sm: 12, xs: 12 }}>
            <Typography
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_ADDRESS")}
            </Typography>
            <TextField
              fullWidth
              placeholder={i18next.t("Add your address")}
              sx={{ mb: 1, fontWeight: "bold", fontFamily: "Tajawal-Regular" }}
              {...formik.getFieldProps("address")}
              error={formik.touched.address && Boolean(formik.errors.address)}
              helperText={
                formik.touched.address && i18next.t(formik.errors.address)
              }
              FormHelperTextProps={{
                sx: {
                  textAlign: i18next.language === "ar" ? "right" : "left",
                  direction: i18next.language === "ar" ? "rtl" : "ltr",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                },
              }}
            />
          </Grid>
          {/* <Grid item size={{ md: 6, sm: 12, xs: 12 }} mt={2}></Grid> */}
          <Grid item size={{ md: 6, sm: 12, xs: 12 }} mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formik.values.isMerchant}
                  name="isMerchant"
                  onChange={formik.handleChange}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Tajawal-Bold",
                    fontSize: "20px", // Adjust size as needed
                    color: "#121212", // Adjust color if required
                  }}
                >
                  {i18next.t(
                    "USER_INFORMATION_FIELD_LABEL_CHECK_IF_YOU_ARE_MERCHANT"
                  )}
                </Typography>
              }
            />
          </Grid>
          <Grid item size={{ md: 6, sm: 12, xs: 12 }} mt={2}></Grid>

          {/* CR Document */}
          <Grid item size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }} mt={2}>
            <Typography
              variant="body1"
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_CR_DOCUMENT")}
            </Typography>
            <Button
              component="label"
              variant="outlined"
              disabled={!formik.values.isMerchant}
              startIcon={<CloudUploadIcon />}
              sx={{
                color: "#6C757D",
                width: "100%",
                borderColor: "#E0E0E0",
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "16px",
                padding: "10px",
                borderRadius: 1,
                fontFamily: "Tajawal-Regular",
                gap: isArabic ? 2 : 0.2,
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_UPLOAD_DOCUMENT")}
              {/* Hidden file input */}
              <input
                type="file"
                accept=".pdf,.jpg"
                hidden
                disabled={!formik.values.isMerchant}
                onChange={(event) => handleDocumentUpload(event, "crDocument")}
              />
            </Button>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#121212",
                fontFamily: "Tajawal-Regular",
                marginTop: "1%",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_SUPPORTED_DOCUMENT_INFO")}
            </Typography>
            {/* CR Document error */}
            {errorsObject.crDocument && (
              <Typography
                color="error"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                  marginLeft: "1.3%",
                }}
              >
                {i18next.t(errorsObject.crDocument)}
              </Typography>
            )}
            {formik.values.crDocument.fileName && (
              <Typography
                color="error"
                sx={{
                  fontSize: "16px",
                  color: "#121212",
                  fontFamily: "Tajawal-Regular",
                }}
              >
                {formik.values.crDocument.fileName}
              </Typography>
            )}
          </Grid>

          {/* IBAN */}
          <Grid item size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }} mt={2}>
            <Typography
              variant="body1"
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_UPLOAD_IBAN")}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              accept=".pdf,.jpg"
              disabled={!formik.values.isMerchant}
              startIcon={<CloudUploadIcon />}
              sx={{
                color: "#6C757D",
                width: "100%",
                borderColor: "#E0E0E0",
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "16px",
                padding: "10px",
                borderRadius: 1,
                fontFamily: "Tajawal-Regular",
                gap: isArabic ? 2 : 0.2,
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_UPLOAD_DOCUMENT")}
              {/* Hidden file input */}
              <input
                type="file"
                accept=".doc,.docx,.pdf,.jpg"
                hidden
                disabled={!formik.values.isMerchant}
                onChange={(event) => handleDocumentUpload(event, "iban")}
              />
            </Button>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#121212",
                fontFamily: "Tajawal-Regular",
                marginTop: "1%",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_SUPPORTED_DOCUMENT_INFO")}
            </Typography>
            {/* IBAN error */}
            {errorsObject.iban && (
              <Typography
                color="error"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                  marginLeft: "1.3%",
                }}
              >
                {i18next.t(errorsObject.iban)}
              </Typography>
            )}
            {formik.values.iban.fileName && (
              <Typography
                color="error"
                sx={{
                  fontSize: "16px",
                  color: "#121212",
                  fontFamily: "Tajawal-Regular",
                }}
              >
                {formik.values.iban.fileName}
              </Typography>
            )}
          </Grid>

          {/* National ID */}
          <Grid item size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }} mt={2}>
            <Typography
              variant="body1"
              sx={{
                mb: 1,

                fontSize: "20px",
                fontFamily: "Tajawal-Regular",
                color: "#121212",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_UPLOAD_NATIONAL_ID")}
            </Typography>
            <Button
              fullWidth
              component="label"
              variant="outlined"
              disabled={!formik.values.isMerchant}
              startIcon={<CloudUploadIcon />}
              sx={{
                color: "#6C757D",
                width: "100%",
                borderColor: "#E0E0E0",
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "16px",
                padding: "10px",
                borderRadius: 1,
                fontFamily: "Tajawal-Regular",
                gap: isArabic ? 2 : 0.2, // <-- Adds space between icon and text
                // flexDirection: i18next.dir() === "rtl" ? "row-reverse" : "row", // Flip icon/text in RTL
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_LABEL_UPLOAD_DOCUMENT")}

              <input
                type="file"
                accept=".pdf,.jpg"
                hidden
                disabled={!formik.values.isMerchant}
                onChange={(event) => handleDocumentUpload(event, "nationalId")}
              />
            </Button>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#121212",
                fontFamily: "Tajawal-Regular",
                marginTop: "1%",
              }}
            >
              {i18next.t("USER_INFORMATION_FIELD_SUPPORTED_DOCUMENT_INFO")}
            </Typography>
            {/* national id error */}
            {errorsObject.nationalId && (
              <Typography
                color="error"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Tajawal-Regular",
                  marginTop: "1%",
                  marginLeft: "1.3%",
                }}
              >
                {i18next.t(errorsObject.nationalId)}
              </Typography>
            )}
            {formik.values.nationalId.fileName && (
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#121212",
                  fontFamily: "Tajawal-Regular",
                }}
              >
                {formik.values.nationalId.fileName}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            size={{ xl: 6, lg: 6, md: 6, sm: 12, xs: 12 }}
            mt={2}
          ></Grid>

          {/* Privacy Policy Checkbox */}
          <Grid item size={{xs:12}} mt={2}>
            <FormControlLabel
              sx={{ fontFamily: "Tajawal-Regular" }}
              control={
                <Checkbox
                  checked={formik.values.privacyPolicy}
                  {...formik.getFieldProps("privacyPolicy")}
                />
              }
              label={
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: isArabic ? 2 : 0.5,
                    fontFamily: "Tajawal-Regular",
                    fontSize: "14px",
                  }}
                >
                  {i18next.t("USER_INFORMATION_FIELD_LABEL_ACCEPT_TERMS_1")}
                  <Link
                    to="#"
                    style={{
                      textDecoration: "underline",
                      color: "#3173B1",
                      fontFamily: "Tajawal-Regular",
                      fontSize: "14px",
                    }}
                  >
                    {i18next.t("USER_INFORMATION_FIELD_LABEL_ACCEPT_TERMS_2")}
                  </Link>
                </Box>
              }
            />
            {formik.touched.privacyPolicy && formik.errors.privacyPolicy && (
              <Typography
                color="error"
                sx={{
                  fontSize: "12px",
                  fontFamily: "Tajawal-Regular",
                  mt: 0.5,
                  ml: 1.5,
                }}
              >
                {i18next.t(formik.errors.privacyPolicy)}
              </Typography>
            )}
          </Grid>

          {/* Submit Button */}
          <Grid item size={{ xs: 12 }} textAlign="center" mt={2}>
            <Box sx={{ textAlign: "center", justifyContent: "center" }}>
              <Button
                // variant="contained"
                type="submit"
                sx={{
                  color: "white",
                  marginTop: "1%",
                  marginBottom: "5%",
                  width: screenWidth > 600 ? "30%" : "50%",
                  fontSize: screenWidth > 600 ? "32px" : "24px",
                  fontWeight: 700,
                  borderTopLeftRadius: "15px",
                  borderBottomRightRadius: "15px",
                  background:
                    "linear-gradient(to right, #004D8A, #02599C, #0886E1, #0A91F2)",
                  fontFamily: "Tajawal-Bold",
                }}
                // onClick={onUserContinue}
              >
                {i18next.t("USER_INFORMATION_PAGE_BUTTON_LABEL_CONTINUE")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
}
